import React, { useEffect, useMemo, useState } from "react";
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
} from "@mui/material";
import { formatIsoDate } from "../../../utils/namesFormater";
import { useDistinctPeriods } from "../../../hooks/useDistinctPeriods";

interface AllDataReportedPeriodProps {
  onChange: (period: string, monthsBack?: number) => void;
  showPolicyCount?: boolean; // הצגת מספר הפוליסות
  enableMonthsFilter?: boolean;
  // תקופה מועדפת לבחירה כברירת מחדל (למשל תאריך שחולץ משם קובץ) - נבחרת רק אם
  // היא קיימת ברשימת התקופות שהתקבלו מה-DB; אחרת נופלים חזרה לתקופה החדשה ביותר.
  preferredPeriod?: string;
  // רשימת תקופות חיצונית (למשל תקופות של עמלות היקף) במקום התקופות של המימשק
  periods?: { reported_period: string; policy_count: number }[];
}

export const AllDataReportedPeriod: React.FC<AllDataReportedPeriodProps> = ({
  onChange,
  showPolicyCount = true,
  enableMonthsFilter = false,
  preferredPeriod,
  periods: externalPeriods,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [monthsBack, setMonthsBack] = useState<number>(3);

  // שימוש ב-React Query hook לטעינת תקופות
  const { data: distinctPeriods = [], isLoading: distinctLoading } = useDistinctPeriods(!externalPeriods);
  const periods = externalPeriods ?? distinctPeriods;
  const loading = externalPeriods ? false : distinctLoading;

  // מיון התקופות לפי תאריך (מהחדש לישן)
  const sortedPeriods = useMemo(() => {
    return [...periods].sort((a, b) =>
      b.reported_period.localeCompare(a.reported_period)
    );
  }, [periods]);

  useEffect(() => {
    if (sortedPeriods.length > 0 && !selectedPeriod) {
      const preferredMatch = preferredPeriod
        ? sortedPeriods.find((p) => p.reported_period === preferredPeriod)
        : undefined;
      const initial = preferredMatch ?? sortedPeriods[0];
      setSelectedPeriod(initial.reported_period);

      const defaultMonths = sortedPeriods.length >= 3 ? 3 : 1;
      setMonthsBack(defaultMonths);

      onChange(initial.reported_period, enableMonthsFilter ? defaultMonths : undefined);
    }
  }, [sortedPeriods, enableMonthsFilter, selectedPeriod, onChange, preferredPeriod]);

  const handlePeriodChange = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setSelectedPeriod(value);
    onChange(value, enableMonthsFilter ? monthsBack : undefined);
  };

  const handleMonthsBackChange = (e: SelectChangeEvent<number>) => {
    const value = e.target.value as number;
    setMonthsBack(value);
    onChange(selectedPeriod, value === 0 ? undefined : value);
  };

  if (loading) return <CircularProgress size={24} />;

  if (sortedPeriods.length === 0) {
    return <Typography variant="body2">לא נמצאו תקופות</Typography>;
  }

  return (
    <Box display="flex" gap={2} alignItems="center">
      <Select
        value={selectedPeriod}
        onChange={handlePeriodChange}
        displayEmpty
        sx={{ minWidth: 180, borderRadius: 4, "& .MuiSelect-select": { fontSize: "18px" } }}
      >
        {sortedPeriods.map((period) => (
          <MenuItem key={period.reported_period} value={period.reported_period}>
            {formatIsoDate(period.reported_period)}
            {showPolicyCount && ` (${period.policy_count})`}
          </MenuItem>
        ))}
      </Select>

      {enableMonthsFilter && (
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>חודשים אחורה</InputLabel>
          <Select
            value={monthsBack}
            onChange={handleMonthsBackChange}
            label="חודשים אחורה"
            sx={{ borderRadius: 4 }}
          >
            <MenuItem value={1}>חודש נוכחי</MenuItem>
            <MenuItem value={3}>3 חודשים</MenuItem>
            <MenuItem value={6}>6 חודשים</MenuItem>
            <MenuItem value={9}>9 חודשים</MenuItem>
            <MenuItem value={12}>12 חודשים</MenuItem>
            <MenuItem value={0}>הכל</MenuItem>
          </Select>
        </FormControl>
      )}
    </Box>
  );
};
